import { NewsArticleComment } from '@models/news-article';
import { patchState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { catchError, EMPTY, pipe, switchMap, tap } from 'rxjs';
import { sortAlphabetically } from '../../helpers';
import { NewsArticlesService } from '../news-articles.service';
import { NewsArticlesStore, sortNewsByDateTime } from '../news-articles.store';

export const getNewsArticleCommentMethods = (injects: { store: NewsArticlesStore; newsArticlesService: NewsArticlesService }) => {
  const { store, newsArticlesService } = injects;
  const getAllNewsArticleComments = rxMethod<{ articleId: string; includeSubComments: boolean; communityId?: string }>(
    pipe(
      tap(() => patchState(store, { isNewsArticleCommentsLoading: true })),
      switchMap(({ articleId, includeSubComments, communityId }) =>
        newsArticlesService.getAllNewsArticleComments(articleId, includeSubComments, communityId).pipe(
          tap(newsArticleComments => {
            patchState(store, {
              newsArticleComments: sortAlphabetically(newsArticleComments, sortNewsByDateTime),
              isNewsArticleCommentsLoading: false,
            });
          }),
          catchError(() => {
            patchState(store, { isNewsArticleCommentsLoading: false });
            return EMPTY;
          }),
        ),
      ),
    ),
  );

  const deleteNewsArticleComment = rxMethod<{ articleId: string; commentId: string }>(
    pipe(
      tap(() => patchState(store, { isNewsArticleCommentsLoading: true })),
      switchMap(({ articleId, commentId }) =>
        newsArticlesService.deleteComment(articleId, commentId).pipe(
          tap(() => {
            patchState(store, {
              newsArticleComments: findAndRemoveComment(store.newsArticleComments(), comment => comment.id === commentId),
              isNewsArticleCommentsLoading: false,
            });
          }),
          catchError(() => {
            patchState(store, { isNewsArticleCommentsLoading: false });
            return EMPTY;
          }),
        ),
      ),
    ),
  );
  return {
    getAllNewsArticleComments,
    deleteNewsArticleComment,
  };
};

export interface NewsArticleCommentsStoreType {
  readonly getAllNewsArticleComments: (params: { articleId: string; includeSubComments: boolean; communityId?: string }) => void;
  readonly deleteNewsArticleComment: (params: { articleId: string; commentId: string }) => void;
}

export function findAndRemoveComment(
  comments: NewsArticleComment[],
  predicate: (comment: NewsArticleComment) => boolean,
): NewsArticleComment[] {
  return comments.reduce((acc, comment) => {
    if (!predicate(comment)) {
      const newComment = { ...comment };

      if (newComment.comments) {
        newComment.comments = findAndRemoveComment(newComment.comments, predicate);
      }

      acc.push(newComment);
    }
    return acc;
  }, [] as NewsArticleComment[]);
}
